import { ConsentState, ConsentCategory } from "./types";
import { getCookie, setCookie } from "../../helpers/cookie";

const ATTRIBUTE_CATEGORY = "data-cookiecategory";

/**
 * The Gatekeeper. This class runs immediately in the <head> to manage
 * consent and script execution before React hydrates.
 */
export default class ConsentManager {
  private static COOKIE_NAME = "theme_cookie_consent";
  private currentConsent: ConsentState | null = null;

  constructor() {
    this.currentConsent = ConsentManager.getSavedConsent();
  }

  /**
   * Safely parses the Consent Object from the cookie.
   * Static so it can be used by Redux slice for initial state.
   */
  public static getSavedConsent(): ConsentState | null {
    const raw = getCookie(ConsentManager.COOKIE_NAME);
    if (!raw) return null;

    try {
      const parsed = JSON.parse(raw) as ConsentState;
      // Basic validation to ensure the object has the expected shape
      if (
        typeof parsed.analytics === "boolean" &&
        typeof parsed.marketing === "boolean"
      ) {
        return parsed;
      }
      return null;
    } catch (e) {
      console.error("[CookieConsent] Failed to parse consent cookie", e);
      return null;
    }
  }

  /**
   * Initializes the manager on page load.
   */
  public init() {
    if (this.currentConsent) {
      this.applyConsent(this.currentConsent);
    }
  }

  /**
   * Returns the current consent state.
   */
  public getConsent(): ConsentState | null {
    return this.currentConsent;
  }

  /**
   * Called by the React UI to update consent and activate scripts.
   */
  public update(newConsent: ConsentState) {
    this.currentConsent = newConsent;

    // --- EXPIRATION LOGIC ---
    // If user accepted ANY optional category, store for 1 year (365 days).
    // If user rejected ALL optional categories, store for 1 week (7 days).
    const hasAcceptedOptional = newConsent.analytics || newConsent.marketing;

    const expiryDays = hasAcceptedOptional ? 365 : 7;

    setCookie(
      ConsentManager.COOKIE_NAME,
      JSON.stringify(newConsent),
      expiryDays
    );

    this.applyConsent(newConsent);

    window.dispatchEvent(
      new CustomEvent("theme_consent_updated", { detail: newConsent })
    );
  }

  /**
   * Scans the DOM for masquerading scripts and activates them based on consent.
   */
  private applyConsent(consent: ConsentState) {
    const categories: ConsentCategory[] = ["analytics", "marketing"];

    categories.forEach((category) => {
      if (consent[category]) {
        this.activateScripts(category);
      }
    });
  }

  private activateScripts(category: string) {
    const scripts = document.querySelectorAll<HTMLScriptElement>(
      `script[type="text/plain"][${ATTRIBUTE_CATEGORY}="${category}"]`
    );

    if (scripts.length > 0) {
      console.log(
        `[ConsentManager] Activating ${scripts.length} script(s) for category: ${category}`
      );
    }

    scripts.forEach((originalScript) => {
      const newScript = document.createElement("script");
      newScript.type = "text/javascript";

      Array.from(originalScript.attributes).forEach((attr) => {
        if (
          attr.name !== "type" &&
          attr.name !== ATTRIBUTE_CATEGORY &&
          attr.name !== "data-src"
        ) {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      if (originalScript.dataset.src) {
        newScript.src = originalScript.dataset.src;
      } else if (originalScript.src) {
        newScript.src = originalScript.src;
      }

      if (originalScript.innerHTML) {
        newScript.text = originalScript.innerHTML;
      }

      originalScript.parentNode?.insertBefore(newScript, originalScript);
      originalScript.remove();
    });
  }
}

// Singleton Instance attached to window
const manager = new ConsentManager();
window.ThemeConsent = {
  init: () => manager.init(),
  update: (c) => manager.update(c),
  getConsent: () => manager.getConsent(),
};

// Auto-init on import
manager.init();
